import { Component, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { AuthService } from "../../../services/auth.service";
import { SnackbarService } from "../../../services/snackbar.service";

@Component({
  selector: "app-password-reset",
  templateUrl: "./password-reset.component.html",
  styleUrls: ["./password-reset.component.css"],
})
export class PasswordResetComponent implements OnInit {
  message: string;
  isAuthenticating = false;
  usernameURL: string = null;
  resetHash: string = null;
  logoPath = `assets/img/${environment.name}/logo.png`;

  // FORM CONTROLS
  username = new FormControl({ value: "", disabled: true }, [
    Validators.required,
    Validators.minLength(3),
  ]);

  oldPassword = new FormControl("");
  newPassword = new FormControl("", []);
  newPasswordConfirmation = new FormControl("", []);

  // FORM GROUPS
  login = new FormGroup({
    username: this.username,
    oldPassword: this.oldPassword,
    newPassword: this.newPassword,
    newPasswordConfirmation: this.newPasswordConfirmation,
  });

  constructor(
    private authService: AuthService,
    private router: Router,
    private snackBarService: SnackbarService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.usernameURL = this.route.snapshot.paramMap.get("login");
    this.resetHash = this.route.snapshot.paramMap.get("resetHash");

    this.newPassword.setValidators([
      Validators.required,
      Validators.minLength(8),
      this.passwordComplexityValid(this.newPassword),
    ]);

    this.newPasswordConfirmation.setValidators([
      Validators.required,
      Validators.minLength(8),
      this.newPasswordConfirmationValid(this.newPassword),
    ]);

    if (!this.isPasswordForgot()) {
      this.oldPassword.setValidators([
        Validators.required,
        Validators.minLength(8),
      ]);
    }

    if (this.usernameURL != null) {
      this.username.setValue(this.usernameURL);
    } else {
      this.username.enable();
    }
  }

  resetPassword() {
    this.username.markAsDirty();
    if (!this.isPasswordForgot()) {
      this.oldPassword.markAsDirty();
    }
    this.newPassword.markAsDirty();
    this.newPasswordConfirmation.markAsDirty();

    if (!this.login.valid) {
      return;
    }

    this.isAuthenticating = true;

    if (this.newPassword.value === this.newPasswordConfirmation.value) {
      this.authService
        .resetPassword(
          this.username.value,
          this.oldPassword.value,
          this.newPassword.value,
          this.resetHash
        )
        .pipe(finalize(() => (this.isAuthenticating = false)))
        .subscribe(
          (result) => {
            if (result) {
              this.router.navigateByUrl("/");
              return;
            }

            this.snackBarService.open("Usuário ou senha inválidos", "OK");
          },
          (result) => {
            if (result.status === 400) {
              if (result.error.message) {
                this.snackBarService.open(result.error.message, "OK");
              } else {
                this.snackBarService.open("Usuário ou senha inválidos", "OK");
              }
            } else if (
              result.status === 401 &&
              result.error &&
              result.error.message === "password-history"
            ) {
              this.snackBarService.open(
                "A nova senha já foi utilizada anteriormente.",
                "OK"
              );
            } else if (result.status === 403) {
              if (!this.isPasswordForgot()) {
                this.snackBarService.open(
                  "Usuário não existe ou bloqueado",
                  "OK"
                );
              } else {
                this.snackBarService.open(
                  "Usuário ou cadeia de verificação inválidos",
                  "OK"
                );
              }
            } else if (result.message) {
              this.snackBarService.open(result.message, "OK");
            } else if (result.error) {
              const {
                error: { message },
              } = result;
              this.snackBarService.open(message, "OK");
            }
          }
        );
    } else {
      this.snackBarService.open(
        "A confirmação de senha não bate com a nova senha.",
        "OK"
      );

      this.isAuthenticating = false;
    }
  }

  newPasswordConfirmationValid(controlNewPassword: FormControl): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (
        control.value !== undefined &&
        control.value !== controlNewPassword.value
      ) {
        return { newPasswordConfirmation: true };
      }
      return null;
    };
  }

  passwordComplexityValid(password: FormControl): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      let complexityMet = 0;
      if (/[a-z]/.test(password.value)) {
        complexityMet++;
      }
      if (/[A-Z]/.test(password.value)) {
        complexityMet++;
      }
      if (/[0-9]/.test(password.value)) {
        complexityMet++;
      }
      if (/[@#&]/.test(password.value)) {
        complexityMet++;
      }

      if (complexityMet < 3) {
        return { passwordComplexity: true };
      }
      return null;
    };
  }

  isPasswordForgot() {
    return this.resetHash !== null;
  }
}
